import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import type { AgencyAuthSessionV1 } from "@enrein/shared-contracts";
import { createStudioAuthClient } from "./auth-api.js";
import { StudioLoginPanel } from "./StudioLoginPanel.js";
import { requestGreenfieldAgencyLogin } from "./features/enrein-greenfield-agency-studio/greenfield-login.js";
import { readStudioRuntimeConfig } from "./env.js";
import { EnvBanner } from "./EnvBanner.js";
import { EnreinGuidedApp } from "./features/enrein-guided-ui/EnreinGuidedApp.js";
import { EnreinV10App } from "./features/enrein-v10-ui/EnreinV10App.js";
import { EnreinGreenfieldApp } from "./features/enrein-greenfield-agency-studio/EnreinGreenfieldApp.js";
import { GreenfieldBootShell } from "./features/enrein-greenfield-agency-studio/chrome/GreenfieldBootShell.js";
import { GuidedLoadingShell } from "./features/enrein-guided-ui/chrome/GuidedToast.js";
import { resolveStudioAgencyId } from "./resolve-studio-agency-id.js";
import {
  bootstrapStudioSession,
  canMountProtectedStudioShell,
  type StudioSessionBootstrapResult,
} from "./session-bootstrap.js";

if (typeof document !== "undefined") {
  const variant = readStudioRuntimeConfig().studioUiVariant;
  if (variant === "guided" || variant === "v142") {
    void import("./features/enrein-guided-ui/styles/guided-ui.css");
  }
}

type StudioAuthView =
  | { readonly phase: "loading" }
  | { readonly phase: "local-dev" }
  | ({ readonly phase: "bootstrapped" } & StudioSessionBootstrapResult);

function StudioRecoveryPanel({ onRetry }: { readonly onRetry: () => void }) {
  return (
    <div className="studio-session-recovery" data-studio-session-state="recoverable-error">
      <h1>Studio could not confirm your session</h1>
      <p>Try again. If this continues, sign in again from a trusted Studio address.</p>
      <button type="button" data-studio-session-retry="true" onClick={onRetry}>
        Try again
      </button>
    </div>
  );
}

function wrapState(state: string, node: ReactNode): ReactNode {
  return <div data-studio-session-state={state}>{node}</div>;
}

export function App() {
  const baseConfig = useMemo(() => readStudioRuntimeConfig(), []);
  const [authView, setAuthView] = useState<StudioAuthView>({ phase: "loading" });
  const [authSession, setAuthSession] = useState<AgencyAuthSessionV1 | null>(null);
  const auth = useMemo(() => {
    const client = createStudioAuthClient(baseConfig);
    if (baseConfig.studioUiVariant !== "greenfield") return client;
    return {
      ...client,
      requestLogin: (email: string) => requestGreenfieldAgencyLogin(baseConfig.apiBaseUrl, email),
    };
  }, [baseConfig]);
  const resolvedAgencyId = useMemo(
    () =>
      resolveStudioAgencyId({
        sessionAgencyId: authSession?.agencyId,
        configAgencyId: baseConfig.agencyId,
        useDevAgencyHeader: baseConfig.useDevAgencyHeader,
      }),
    [authSession?.agencyId, baseConfig.agencyId, baseConfig.useDevAgencyHeader],
  );

  const resolveAuthView = useCallback(async (): Promise<StudioAuthView> => {
    if (baseConfig.useDevAgencyHeader) {
      return { phase: "local-dev" };
    }
    if (!baseConfig.apiBaseUrl) {
      return { phase: "bootstrapped", state: "unauthenticated", reason: "missing" };
    }
    const result = await bootstrapStudioSession({ getSession: () => auth.getSession() });
    return { phase: "bootstrapped", ...result };
  }, [auth, baseConfig.apiBaseUrl, baseConfig.useDevAgencyHeader]);

  const applyAuthView = useCallback((view: StudioAuthView) => {
    setAuthView(view);
    setAuthSession(
      view.phase === "bootstrapped" && view.state === "authenticated" ? view.session : null,
    );
  }, []);

  const loadSession = useCallback(async () => {
    applyAuthView(await resolveAuthView());
  }, [applyAuthView, resolveAuthView]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const view = await resolveAuthView();
      if (!cancelled) {
        applyAuthView(view);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applyAuthView, resolveAuthView]);

  const handleAuthenticated = useCallback(async () => {
    await loadSession();
  }, [loadSession]);

  const isGreenfield = baseConfig.studioUiVariant === "greenfield";

  if (authView.phase === "loading") {
    return wrapState("loading", isGreenfield ? <GreenfieldBootShell /> : <GuidedLoadingShell />);
  }

  if (authView.phase === "bootstrapped" && authView.state === "recoverable-error") {
    const recovery = <StudioRecoveryPanel onRetry={() => void loadSession()} />;
    return isGreenfield ? (
      <GreenfieldBootShell message="Studio could not confirm your session">{recovery}</GreenfieldBootShell>
    ) : (
      <GuidedLoadingShell message="Studio could not confirm your session">{recovery}</GuidedLoadingShell>
    );
  }

  const authenticated =
    authView.phase === "local-dev" ||
    (authView.phase === "bootstrapped" && canMountProtectedStudioShell(authView));

  if (!authenticated) {
    const login = <StudioLoginPanel auth={auth} onAuthenticated={() => void handleAuthenticated()} />;
    return wrapState(
      "unauthenticated",
      isGreenfield ? (
        <GreenfieldBootShell message="Sign in to open Studio">{login}</GreenfieldBootShell>
      ) : (
        <GuidedLoadingShell message="Sign in to open Studio">{login}</GuidedLoadingShell>
      ),
    );
  }

  const stagingBanner =
    baseConfig.deployEnv === "staging" ? <EnvBanner deployEnv="staging" /> : null;

  const protectedShell = (children: ReactNode) => (
    <div data-studio-session-state="authenticated" data-studio-protected-shell="true">
      {stagingBanner}
      {children}
    </div>
  );

  if (isGreenfield) {
    return protectedShell(
      <EnreinGreenfieldApp
        agencyId={resolvedAgencyId}
        onLogout={async () => {
          await auth.logout();
          applyAuthView({ phase: "bootstrapped", state: "unauthenticated", reason: "missing" });
        }}
      />,
    );
  }

  if (baseConfig.studioUiVariant === "v10") {
    return protectedShell(<EnreinV10App agencyId={resolvedAgencyId} />);
  }

  if (!baseConfig.v142NewUiEnabled) {
    return (
      <GuidedLoadingShell message="Studio UI is disabled (rollback). Remove VITE_ENREIN_V142_NEW_UI=0 or set it to 1 to restore the default UI." />
    );
  }

  return protectedShell(<EnreinGuidedApp agencyId={resolvedAgencyId} />);
}
